import type { Dispatch } from "react";
import type { NotificationPushPayload } from "@tc-superpods/app-contract";
import { errorNotificationDurationMs, standardNotificationDurationMs } from "./config.js";
import type { ShellAction } from "./state";

const defaultDurationByLevel: Record<NotificationPushPayload["level"], number> = {
  success: standardNotificationDurationMs,
  error: errorNotificationDurationMs,
  info: standardNotificationDurationMs,
  warning: standardNotificationDurationMs
};

export interface NotificationController {
  push: (payload: NotificationPushPayload) => void;
  dismiss: (id: string) => void;
  dispose: () => void;
}

export function createNotificationController(
  dispatch: Dispatch<ShellAction>
): NotificationController {
  const timers = new Map<string, number>();
  const live = new Set<string>();
  let sequence = 0;

  function push(payload: NotificationPushPayload): void {
    const id = `notification-${(sequence += 1)}`;
    live.add(id);
    dispatch({
      type: "notification-add",
      notification: { ...payload, id }
    });

    const durationMs = typeof payload.durationMs === "number"
      && Number.isFinite(payload.durationMs) && payload.durationMs >= 0
      ? payload.durationMs
      : defaultDurationByLevel[payload.level];

    if (durationMs > 0) {
      const timeoutId = window.setTimeout(() => {
        dismiss(id);
      }, durationMs);
      timers.set(id, timeoutId);
    }
  }

  function dismiss(id: string): void {
    if (!live.has(id)) {
      return;
    }

    live.delete(id);
    clearTimer(id);
    dispatch({ type: "notification-remove", id });
  }

  function clearTimer(id: string): void {
    const timeoutId = timers.get(id);

    if (timeoutId === undefined) {
      return;
    }

    window.clearTimeout(timeoutId);
    timers.delete(id);
  }

  function dispose(): void {
    for (const id of timers.keys()) {
      clearTimer(id);
    }
    live.clear();
  }

  return {
    push,
    dismiss,
    dispose
  };
}
